import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../hooks/axios';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import AdminPageShell from '../../components/admin/AdminPageShell';
import AdminCard from '../../components/admin/AdminCard';
import AdminFormActions from '../../components/admin/AdminFormActions';
import InputWithLabel from '../../components/form/input/InputWithLabel';
import {
  adminAlertErrorClass,
  adminFormFullWidthClass,
  adminFormGridClass,
} from '../../components/admin/adminStyles';
import { placeTypeLabel, type SeoPlaceRecord } from './seoPlaceTypes';

type FormState = {
  name: string;
  fullName: string;
  iata: string;
  city: string;
  county: string;
  region: string;
  metaTitle: string;
  metaDescription: string;
  h1: string;
  intro: string;
  keywords: string;
  published: boolean;
};

const INITIAL: FormState = {
  name: '',
  fullName: '',
  iata: '',
  city: '',
  county: '',
  region: '',
  metaTitle: '',
  metaDescription: '',
  h1: '',
  intro: '',
  keywords: '',
  published: true,
};

export default function SeoPlaceForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [place, setPlace] = useState<SeoPlaceRecord | null>(null);
  const [form, setForm] = useState<FormState>(INITIAL);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    axiosInstance
      .get(`/seo-places/${id}`)
      .then((res) => {
        const data = res.data as SeoPlaceRecord;
        setPlace(data);
        setForm({
          name: data.name ?? '',
          fullName: data.fullName ?? '',
          iata: data.iata ?? '',
          city: data.city ?? '',
          county: data.county ?? '',
          region: data.region ?? '',
          metaTitle: data.seo.metaTitle ?? '',
          metaDescription: data.seo.metaDescription ?? '',
          h1: data.seo.h1 ?? '',
          intro: data.seo.intro ?? '',
          keywords: (data.seo.keywords ?? []).join(', '),
          published: data.published ?? true,
        });
      })
      .catch(() => setError('Failed to load place'));
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      await axiosInstance.put(`/seo-places/${id}`, {
        name: form.name.trim(),
        fullName: form.fullName.trim() || null,
        iata: form.iata.trim() || null,
        city: form.city.trim() || null,
        county: form.county.trim() || null,
        region: form.region.trim() || null,
        published: form.published,
        seo: {
          metaTitle: form.metaTitle.trim() || null,
          metaDescription: form.metaDescription.trim() || null,
          h1: form.h1.trim() || null,
          intro: form.intro.trim() || null,
          keywords: form.keywords
            .split(',')
            .map((k) => k.trim())
            .filter(Boolean),
        },
      });
      navigate('/seo-places');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } })
        ?.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join(', ') : msg || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminPageShell>
      <PageBreadcrumb
        pageTitle="Edit SEO Place"
        description={
          place
            ? `${placeTypeLabel(place.placeType)} · ${place.slug}`
            : 'Update place SEO overrides'
        }
      />

      <AdminCard>
        {error && <div className={`mb-4 ${adminAlertErrorClass}`}>{error}</div>}
        <form onSubmit={handleSubmit} className={`${adminFormGridClass} w-full`}>
          <div className={adminFormFullWidthClass}>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Places are shared across all brand sites. SEO overrides apply to
              airport, location, and cruise port detail pages.
            </p>
          </div>

          <InputWithLabel
            label="Name"
            name="name"
            value={form.name}
            onChange={handleChange}
            required
          />
          <InputWithLabel
            label="Full name"
            name="fullName"
            value={form.fullName}
            onChange={handleChange}
          />
          <InputWithLabel
            label="IATA"
            name="iata"
            value={form.iata}
            onChange={handleChange}
          />
          <InputWithLabel
            label="City"
            name="city"
            value={form.city}
            onChange={handleChange}
          />
          <InputWithLabel
            label="County"
            name="county"
            value={form.county}
            onChange={handleChange}
          />
          <InputWithLabel
            label="Region"
            name="region"
            value={form.region}
            onChange={handleChange}
          />

          <InputWithLabel
            label="Meta title"
            name="metaTitle"
            value={form.metaTitle}
            onChange={handleChange}
          />
          <div className={adminFormFullWidthClass}>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Meta description
            </label>
            <textarea
              name="metaDescription"
              value={form.metaDescription}
              onChange={handleChange}
              rows={3}
              className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
          </div>
          <InputWithLabel label="H1" name="h1" value={form.h1} onChange={handleChange} />
          <div className={adminFormFullWidthClass}>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Intro paragraph
            </label>
            <textarea
              name="intro"
              value={form.intro}
              onChange={handleChange}
              rows={4}
              className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
          </div>
          <div className={adminFormFullWidthClass}>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Keywords (comma-separated)
            </label>
            <textarea
              name="keywords"
              value={form.keywords}
              onChange={handleChange}
              rows={2}
              className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 sm:col-span-2 xl:col-span-3">
            <input
              type="checkbox"
              name="published"
              checked={form.published}
              onChange={handleChange}
            />
            <span>Published</span>
          </label>

          <div className={adminFormFullWidthClass}>
            <AdminFormActions listRoute="/seo-places" saving={loading} />
          </div>
        </form>
      </AdminCard>
    </AdminPageShell>
  );
}
